import type {
    AlertProps,
    IconName,
    RichTextProps,
} from '@myphx-engineering/phx-design-system';
import type { SemanticColorVariants } from '@myphx-engineering/phx-design-system/framework';
import type { HeadlinePriceType } from '@phx/data-sources/medication-marketplace';

import type { FillPathway } from '../contexts/fill';
import type { FlagSet } from '../generated/feature-flags';
import type {
    HeadlinePrice,
    MedicationConfigOverride,
    MedicationOverride,
    PathwayOverride,
    PathwayScreen,
} from '../hooks/use-config-api';
import type { NdcSwitchConfig, NdcSwitchConfigV2 } from '../types/drug-config';
import type { DrugKeyType } from '../util/config/constants';

export type PathwayOptionId =
    | 'commercial'
    | 'government'
    | 'self-pay'
    | 'medicare-bridge'
    | 'medicare-part-d'
    | 'other-government'
    | 'retail-price';

export type CardBullet = {
    key: string;
    icon: IconName;
    color: SemanticColorVariants;
    text: string; // supports inline HTML: <b>, <br />
};

export type PathwayCardContent = {
    heading: string;
    cardColor?: SemanticColorVariants;
    headerIcon?: IconName;
    // Conditional sub-headings — fall back left-to-right based on savings card state
    subHeading?: string; // default / no card present
    subHeadingWithCard?: string; // non-commercial savings card
    subHeadingCommercialCard?: string; // commercial savings card
    bullets?: CardBullet[];
    // Price area — actual value comes from offer data; these control labels/notes
    showPrice?: boolean;
    priceSuperscript?: string; // e.g. '*' or '‡' shown after /month on main price
    fallbackSuperscript?: string; // e.g. '‡' shown after /month on the fallback subcard
    noPriceText?: string; // shown in place of price when showPrice is false
    priceLabel?: string; // e.g. "Pay as low as"
    priceNote?: string; // rendered via RichText — supports <b>, <br />, <a href="...">
    disclaimer?: string; // shown below the gray subcard (or below price when no gray subcard)
    fallbackPriceLabel?: string;
    fallbackDisclaimer?: string; // disclaimer text rendered inside the gray fallback subcard
    fallbackTermsUrl?: string; // "See terms and conditions" link rendered inside the gray fallback subcard
    priceWithCardNote?: string; // shown below price when offer is a savings card (CashAssistance/Incentive)
    button: string;
    buttonVariant?: 'filled' | 'outline'; // defaults to 'filled'
};

export type PathwayOptionConfig = {
    id: PathwayOptionId;
    fillPathway: FillPathway;
    headlinePriceType: HeadlinePriceType;
    subPathway?: string;
    recommended?: boolean;
    destination?: Extract<PathwayScreen, 'attestation' | 'pharmacy-select'>;
    content: PathwayCardContent;
};

export type MedicareBridgeInfoPanel = {
    icon: IconName;
    heading: string;
    description: string;
    outcomesLabel?: string;
};

// A card set with optional screen heading/description shown above the cards
export type PathwayOptionSet = {
    heading?: string;
    description?: string;
    descriptionLinkUrl?: string; // URL for <a> tag within description text
    infoPanel?: MedicareBridgeInfoPanel;
    cards: PathwayOptionConfig[];
};

// Different card sets for different arrival contexts on the same drug
export type DrugPathwayOptions = {
    default: PathwayOptionSet;
    insuranceFavored?: PathwayOptionSet; // when pathway=insurance or favoredPathway=insurance
    selfPayFavored?: PathwayOptionSet; // when pathway=self-pay
    medicareBridge?: PathwayOptionSet; // when pathway=government&sub-pathway=medicare-bridge
    governmentInsurance?: PathwayOptionSet; // when pathway=government&sub-pathway=other-government
    medicareBridgeContent?: PathwayOptionSet; // heading + info panel + cards shown above pharmacy list
    showEscapeHatch?:
        | boolean
        | Partial<Record<FillPathway, boolean | Record<string, boolean>>>; // show "Choose a different pharmacy" exit card on pharmacy selection screen
};

export type NdcEntry = {
    ndc: string;
    packageQuantity: number;
};

export type ProductPricing = {
    selfPay?: number;
    regularPrice?: number;
    insurance?: number;
};

type HoldScreenLines = Array<RichTextProps & { key: string }>;

export type OnHoldConfig = {
    allowHold: boolean;
    pathways: FillPathway[];
    subPathways?: string[];
    screens?: PathwayScreen[];
    buttonText: string;
    modal: {
        title: string;
        body: string[];
        buttonLabels: {
            close: string;
            confirm: string;
        };
    };
    holdScreen: {
        alert: {
            color: AlertProps['severity'];
            body: Array<RichTextProps & { key: string }>;
        };
        body: {
            default: HoldScreenLines;
            withChangeReq?:
                | HoldScreenLines
                | Partial<Record<string, HoldScreenLines>>;
        };
        title?:
            | {
                  default: string;
                  withChangeReq?: string;
              }
            | {
                  default?: string;
                  withChangeReq: string;
              };
        buttonLabels: {
            home: string;
            unhold: string;
        };
        toast?:
            | {
                  default: string;
                  withChangeReq?: string;
              }
            | {
                  default?: string;
                  withChangeReq: string;
              };
        confirmModal?: {
            title: string;
            body: string[] | Partial<Record<string, string[]>>;
            checkboxLabel: string;
            buttonLabels: {
                confirm: string;
            };
        };
    };
};

export type ProductDisclaimerSlot =
    | 'outcome-card'
    | 'path-select-card'
    | 'path-select-screen'
    | 'price-card';

export type ProductDisclaimer = {
    content: Array<RichTextProps & { key: string }>;
    headlinePriceTypes: HeadlinePriceType[];
    slots: ProductDisclaimerSlot[];
};

export type ProductDisclosures = {
    disclaimers?: ProductDisclaimer[];
};

export type MedicationProduct = {
    // Metadata fields are optional — omitting them excludes the product from ndcInfo
    // (the configured description won't override the pharmacy API's description).
    description?: string;
    dosageForm?: MedicationOverride['dosageForm'];
    formLabel?: string;
    packageSizeUnit?: string;
    strengthUnit?: string;
    strengthValue?: number;
    imageUrl?: string;
    imageOrientation?: 'portrait' | 'landscape';
    ndcs: NdcEntry[];
    /**
     * @deprecated: use `headlinePrices` instead
     */
    pricing: ProductPricing;
    /**
     * Full switch config if this product can be switched FROM. Omit if not applicable.
     * @deprecated: use `switchConfigV2` instead
     */
    switchConfig?: NdcSwitchConfig;
    switchConfigV2?: NdcSwitchConfigV2;
    onHoldConfig?: OnHoldConfig;
    headlinePrices?: HeadlinePrice[];
    disclosures?: ProductDisclosures;
    pathway?: PathwayOverride;
    metadata?: MedicationOverride['metadata'];
};

export type PathwayAlertConfig = {
    pathways: FillPathway[];
    screens?: PathwayScreen[];
    subPathways?: string[]; // when set, alert only shows if URL sub-pathway matches one of these
    color: AlertProps['severity'];
    content: string;
    featureFlagName?: keyof FlagSet;
    cta?: {
        text: string;
        inline?: boolean;
        patientChoiceKey?: string;
        target: {
            pathway: FillPathway;
            subPathway?: string;
            destination?: Extract<
                PathwayScreen,
                'pharmacy-select' | 'path-select'
            >;
        };
    };
};

export const CLICKED_MEDICARE_BANNER_CHOICE_KEY = 'clicked-medicare-banner';

export type PathwayTerms = {
    heading: string;
    body: string;
    termsUrl?: string;
    savingsCard?: {
        name: string;
        type: string;
        shortDesc: {
            heading: string;
            body: string;
        };
        insuranceRequirement?: 'ANY_COMMERCIAL' | 'COVERED';
    };
    incentiveProgram?: {
        name: string;
        disclaimer?: string;
        terms: string;
        termsUrl?: string;
    };
};

export type ProgramTerms = Partial<Record<FillPathway, PathwayTerms | null>>;

export type ProgramTermsByDrugKey = Partial<
    Record<DrugKeyType, ProgramTerms | null>
>;

// A single selectable option on a coverage questionnaire screen.
// Options either navigate directly (target) or open a nested sub-questionnaire.
export type QuestionnaireOption = {
    id: string;
    content: {
        heading: string;
        description?: string;
    };
    // Navigate to path-select (default) or directly to attestation ('root')
    target?: {
        pathway: FillPathway;
        subPathway?: string;
        destination?: Extract<
            PathwayScreen,
            'attestation' | 'path-select' | 'pharmacy-select'
        >;
    };
    // Show a nested sub-questionnaire screen (e.g. government insurance sub-types)
    subQuestionnaire?: PathwayQuestionnaireConfig;
};

export type PathwayQuestionnaireConfig = {
    heading: string;
    description?: string;
    options: QuestionnaireOption[];
    notFoundText?: string;
    notFoundModal?: {
        heading: string;
        body: string;
        linkUrl?: string;
    };
};

export type ProgramContentData = {
    terms: ProgramTermsByDrugKey;
    pathwayAlerts?: Partial<Record<DrugKeyType, PathwayAlertConfig[]>>;
    pathwayOptions?: Partial<Record<DrugKeyType, DrugPathwayOptions>>;
    pathwayOptionsByNdc?: Record<string, DrugPathwayOptions>;
    pathwayQuestionnaire?: Partial<
        Record<DrugKeyType, PathwayQuestionnaireConfig>
    >;
};

export function buildNdcInfo(
    products: MedicationProduct[]
): Record<string, MedicationOverride> {
    const result: Record<string, MedicationOverride> = {};

    for (const product of products) {
        if (
            product.description === undefined ||
            product.dosageForm === undefined ||
            product.formLabel === undefined ||
            product.packageSizeUnit === undefined
        ) {
            continue;
        }

        for (const { ndc, packageQuantity } of product.ndcs) {
            result[ndc] = {
                id: ndc,
                ndc,
                description: product.description,
                dosageForm: product.dosageForm,
                form: product.formLabel,
                packageQuantity,
                packageSizeUnit: product.packageSizeUnit,
                ...(product.strengthUnit !== undefined && {
                    strengthUnit: product.strengthUnit,
                }),
                ...(product.strengthValue !== undefined && {
                    strengthValue: product.strengthValue,
                }),
                ...(product.imageUrl !== undefined && {
                    imageUrl: product.imageUrl,
                }),
                ...(product.imageOrientation !== undefined && {
                    imageOrientation: product.imageOrientation,
                }),
                headlinePrices: product.headlinePrices ?? [],
                ...(product.pathway !== undefined && {
                    pathway: product.pathway,
                }),
                ...(product.metadata !== undefined && {
                    metadata: product.metadata,
                }),
            };
        }
    }

    return result;
}

export function buildStaticPricing(
    products: MedicationProduct[]
): MedicationConfigOverride['staticPricing'] {
    const insurance: Record<string, number> = {};
    const selfPay: Record<string, number> = {};
    const regularPrices: Record<string, number> = {};
    const nonCoveredBenefit: Record<string, number> = {};

    for (const { ndcs, pricing, headlinePrices } of products) {
        for (const { ndc } of ndcs) {
            if (pricing.insurance !== undefined)
                insurance[ndc] = pricing.insurance;
            if (pricing.selfPay !== undefined) selfPay[ndc] = pricing.selfPay;
            if (pricing.regularPrice !== undefined)
                regularPrices[ndc] = pricing.regularPrice;
            const nonCoveredPrice = headlinePrices?.find(
                (p) => p.type === 'NonCoveredBenefit'
            )?.value;
            if (nonCoveredPrice !== undefined)
                nonCoveredBenefit[ndc] = nonCoveredPrice;
        }
    }

    return {
        ...(Object.keys(insurance).length > 0 && { insurance }),
        ...(Object.keys(selfPay).length > 0 && { selfPay }),
        ...(Object.keys(regularPrices).length > 0 && { regularPrices }),
        ...(Object.keys(nonCoveredBenefit).length > 0 && { nonCoveredBenefit }),
    };
}

export function buildNdcSwitchConfigs(
    products: MedicationProduct[]
): Record<string, NdcSwitchConfig> {
    const result: Record<string, NdcSwitchConfig> = {};

    for (const { ndcs, switchConfig } of products) {
        if (!switchConfig) continue;
        for (const { ndc } of ndcs) {
            result[ndc] = switchConfig;
        }
    }

    return result;
}

export function buildNdcSwitchV2Configs(
    products: MedicationProduct[]
): Record<string, NdcSwitchConfigV2> {
    const result: Record<string, NdcSwitchConfigV2> = {};

    for (const { ndcs, switchConfigV2 } of products) {
        if (!switchConfigV2) continue;
        for (const { ndc } of ndcs) {
            result[ndc] = switchConfigV2;
        }
    }

    return result;
}

export function buildNdcHoldConfigs(
    products: MedicationProduct[]
): Record<string, OnHoldConfig> {
    const result: Record<string, OnHoldConfig> = {};

    for (const { ndcs, onHoldConfig } of products) {
        if (!onHoldConfig) {
            continue;
        }

        for (const { ndc } of ndcs) {
            result[ndc] = onHoldConfig;
        }
    }

    return result;
}

export function buildNdcDisclosureConfigs(
    products: MedicationProduct[]
): Record<string, ProductDisclosures> {
    const result: Record<string, ProductDisclosures> = {};

    for (const { ndcs, disclosures } of products) {
        if (!disclosures) {
            continue;
        }

        for (const { ndc } of ndcs) {
            result[ndc] = disclosures;
        }
    }

    return result;
}

export const getDrugPriceForDose = (
    products: MedicationProduct[],
    strengthValue: number,
    priceType: HeadlinePrice['type']
): number => {
    const product = products.find((p) => p.strengthValue === strengthValue);
    const price = product?.headlinePrices?.find(
        (price) => price.type === priceType
    );

    if (!price) {
        throw new Error('This needs to be a build time error');
    }

    return price.value;
};
